import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@reactuses/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnFiltersState, RowSelectionState, Updater } from "@tanstack/react-table";
import { css } from "../../../../../styled-system/css";
import { HStack, VStack, Wrap } from "../../../../../styled-system/jsx";
import { useAppSelector } from "../../../../redux/hooks";
import { useSimpleCan } from "../../../../config/casl/casl";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { useGetV4TargetReportByTarget } from "../../../../modules/perf_reports/api/target/v4TargetReportByTarget";
import { useGetV4TargetReportOptions } from "../../../../modules/perf_reports/api/target/v4TargetReportOptions";
import { useTagFilterOptions } from "../../../../modules/tags/api/useTagFilterOptions";
import { useTagNameFilterOptions } from "../../../../modules/tags/api/useTagNameFilterOptions";
import { useV4TargetReportExport } from "../../../../modules/perf_reports/api/target/v4TargetReportExport";
import { useFetch } from "../../../../hooks/api";
import { usePersistedTableState } from "../../../table/usePersistedTableState";
import { Table } from "../../../table/Table";
import { RT8Pagination } from "../../../table/Pagination";
import { RT8ColumnSelector } from "../../../core/blocks/MetricSelector";
import { SearchInput } from "../../../fields/base/TextField";
import { RT8FilterWidget } from "../../../../modules/merchandising/components/tables/FilterWidget";
import { BulkActionButton } from "../../../table/BulkActionButton";
import { EditableTableWrapper, EditableTableSubmitArea } from "../../../table/EditableTableWrapper";
import { TableErrorWrapper } from "../../../table/TableErrorWrapper";
import { Button } from "../../../core/basic/Button";
import { MdRotateLeft } from "react-icons/md";
import { resetTable, selectionStateToSelectedIds } from "../../../table/utils";
import { DownloadV4Report } from "../../../../modules/perf_reports/DownloadReport";
import TargetDetails from "../../../../modules/advertising/components/TargetDetails";
import useTargetBulkActions from "../../../../hooks/useTargetBulkActions";
import AddTargetsWidget from "./AddTargetsWidget";
import { useAdTargetColumns } from "./useAdTargetColumns";
import { AD_TYPES, GOALS } from "../../../core/blocks/AdPlan/constants";
import { useUpdateTargetBids } from "../../../../hooks/useUpdateTargetBids";
import { useUpdateAdPlan } from "../../../core/blocks/AdPlan/useUpdateAdPlan";
import { DIMENSION_TYPES, useTargetBidStateOptions } from "../../../../modules/str/utils/reportingConstants";
import {
  KEYWORD_MATCH_TYPE_OPTIONS,
  TARGET_MATCH_TYPE_OPTIONS,
  TARGET_STATE_OPTIONS,
  AMAZON_TARGET_SOURCE_OPTIONS,
  WALMART_TARGET_SOURCE_OPTIONS,
} from "../../../../modules/advertising/filters/targetLifecycleFilters";
import { SimulationParametersForm } from "../../../../modules/advertising/components/BidSimulation/SimulationParametersForm";
import { useBidSimulation } from "./useBidSimulation";
import { DataViewPicker } from "../../../../modules/dataViews/components/DataViewPicker";
import { useServerTable } from "../../../table/tableFeatures";

type AdPlanData = {
  ad_goal: number;
  ad_type: number;
  non_trellis_enrolled_ad: boolean;
  non_trellis_enrolled_ad_accept_bids: boolean;
  serving_status: string;
};

type AdTargetTableProps = {
  adId: number;
  adPlanData: AdPlanData;
  adPlanParams: Record<string, unknown>;
  targetType: "keyword" | "target" | "negative-keyword";
};

export const AdTargetTable = ({ adId, adPlanData, adPlanParams, targetType }: AdTargetTableProps) => {
  const { t } = useTranslation(["advertising", "common"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const userCanUpdateAds = useSimpleCan("update", "Ad");
  const userCanExportAds = useSimpleCan("export", "Ad");
  const queryClient = useQueryClient();
  const isNTMAd = adPlanData.ad_goal === GOALS.IMPORTED_ADS.value;
  const NTMBidsAccepted = adPlanData.non_trellis_enrolled_ad_accept_bids;
  const isTrellisManagedAd = !isNTMAd || NTMBidsAccepted;
  // Until an imported ad's bids are accepted, Trellis' proposed bids are only a preview
  const showPreviewBid = isNTMAd && adPlanData.non_trellis_enrolled_ad && !NTMBidsAccepted;
  const isNegativeKeyword = targetType === "negative-keyword";
  const queryTargetType = isNegativeKeyword ? "keyword" : targetType;

  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");
  const tagEnhancedOptions = useTagFilterOptions("KEYWORD-TARGET", { enabled: tagsEnhancedEnabled });
  const targetReportOptionsQuery = useQuery({
    ...useGetV4TargetReportOptions(queryTargetType),
    enabled: !tagsEnhancedEnabled,
  });
  const tagOptions = useTagNameFilterOptions(targetReportOptionsQuery.data?.data.tags);

  const dimensions = queryTargetType === "target" ? DIMENSION_TYPES.TARGET_ID : DIMENSION_TYPES.KEYWORD_ID;
  const idField = queryTargetType === "target" ? "adv_target_id" : "adv_keyword_id";
  const targetBidStateOptions = useTargetBidStateOptions();

  const negativeKeywordMatchTypeOptions = useMemo(
    () => [
      { value: "negativePhrase", label: t(($) => $.advertising["Negative Phrase"]) },
      { value: "negativeExact", label: t(($) => $.advertising["Negative Exact"]) },
    ],
    [t],
  );

  const {
    simulationMode,
    enterSimulation,
    exitSimulation,
    simulationDataMap,
    simulationInitialValues,
    handleApplySimulation,
    handleDeploy,
    isSimulating,
    isDeploying,
    hasResults,
  } = useBidSimulation({ adId, adPlanParams });

  const { columns, initialVisibility } = useAdTargetColumns({
    targetType,
    adType: adPlanData.ad_type,
    isNTMAd,
    simulationData: simulationMode ? simulationDataMap : undefined,
    showPreviewBid,
  });

  const tableId = `adTargetTable-${targetType}`;
  const { state, initialState, setters } = usePersistedTableState({
    persistKey: tableId,
    initialState: {
      sorting: [{ id: isNegativeKeyword ? "text" : "bid", desc: !isNegativeKeyword }],
      columnVisibility: initialVisibility,
      columnPinning: { start: ["text"], end: [] },
      pagination: { pageIndex: 0, pageSize: 10 },
    },
  });

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedRowsData, setSelectedRowsData] = useState<Record<string, Record<string, unknown>>>({});
  const debounceSearch = useDebounce(state.globalFilter, 500);

  // Ad ID filter for v4 query
  const adIdFilter = useMemo<ColumnFiltersState>(() => [{ id: "ad_id", value: { op: "=", value: adId } }], [adId]);

  // Structural match_type filter for negative keywords
  const negativeMatchTypeFilter = useMemo<ColumnFiltersState>(
    () =>
      isNegativeKeyword ?
        [{ id: "match_type", value: { op: "anyof", value: ["negativePhrase", "negativeExact"] } }]
      : [],
    [isNegativeKeyword],
  );

  const combinedFilters = useMemo(
    () => [...adIdFilter, ...negativeMatchTypeFilter, ...state.columnFilters],
    [adIdFilter, negativeMatchTypeFilter, state.columnFilters],
  );

  // V4 data fetching
  const byTargetOptionsGetter = useGetV4TargetReportByTarget(queryTargetType, dimensions);
  const targetReportQuery = useQuery(
    byTargetOptionsGetter({
      pagination: state.pagination,
      sorting: state.sorting,
      filters: combinedFilters,
      search: debounceSearch,
    }),
  );

  // Prefetch next page
  void queryClient.prefetchQuery({
    ...byTargetOptionsGetter({
      pagination: { pageIndex: state.pagination.pageIndex + 1, pageSize: state.pagination.pageSize },
      sorting: state.sorting,
      filters: combinedFilters,
      search: debounceSearch,
    }),
    staleTime: 10000,
  });

  // Insights data (same as MicroStores2)
  const { data: insightsData } = useFetch(
    ["targetInsights", adId],
    `/target_details`,
    { advertisement: adId, insights: true },
    {
      select: (res: { data?: { insights?: unknown[] } }) => res.data?.insights ?? [],
      staleTime: 5 * 60 * 1000,
    },
  );

  // Merge v4 data with insights
  const insights = insightsData as Record<string, unknown>[] | undefined;
  const tableData = useMemo(() => {
    if (!targetReportQuery.data?.data) return [];
    return targetReportQuery.data.data.map((row) => ({
      ...row,
      ...insights?.find((insight) => insight.adv_target === row[idField] || insight.adv_keyword === row[idField]),
    }));
  }, [targetReportQuery.data?.data, insights, idField]);

  const lockedCount = targetReportQuery.data?.count_details?.locked_count ?? 0;

  const pageLockedCount = useMemo(() => {
    return tableData.filter((row) => row.lockout).length;
  }, [tableData]);

  // Row selection handler — accumulates selected row data across pages
  const handleRowSelectionChange = (updater: Updater<RowSelectionState>) => {
    const newRowSelection = typeof updater === "function" ? updater(rowSelection) : updater;

    const updatedSelectedRowsData = { ...selectedRowsData };

    for (const row of table.getRowModel().rows) {
      const id = row.id;
      if (newRowSelection[id]) {
        updatedSelectedRowsData[id] = {
          tags: row.original.tags,
          target_source: row.original.source,
          scheduled_actions: row.original.scheduled_actions,
          id: parseInt(id),
          target_id: parseInt(id),
        };
      } else {
        // oxlint-disable-next-line typescript/no-dynamic-delete
        delete updatedSelectedRowsData[id];
      }
    }

    setRowSelection(newRowSelection);
    setSelectedRowsData(updatedSelectedRowsData);
  };

  const { submitBids } = useUpdateTargetBids(queryTargetType);
  const updateAdPlan = useUpdateAdPlan({ adId });

  // Export mutation
  const exportMutation = useV4TargetReportExport(queryTargetType, combinedFilters);

  const table = useServerTable({
    data: tableData,
    columns,
    alternateSortingEnabled: true,
    getRowCanExpand: (row) => {
      return typeof row.original[idField] === "number";
    },
    getRowId: (row, index) => {
      const id = row[idField];
      return id?.toString() ?? `row-${index}`;
    },
    enableRowSelection: (row) => {
      if (row.original.lockout) {
        return false;
      }
      const isThemeMatch = row.original.match_type === "theme";
      const isSponsoredProduct = [AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(
        adPlanData.ad_type,
      );
      return !isThemeMatch || isSponsoredProduct;
    },
    rowCount: targetReportQuery.data?.count ?? 0,
    initialState,
    onRowSelectionChange: handleRowSelectionChange,
    state: { ...state, rowSelection },
    ...Object.fromEntries(Object.entries(setters).filter(([key]) => key !== "onRowSelectionChange")),
    enableSelectAllMatching: true,
    meta: {
      showSkeleton: targetReportQuery.isFetching && (!targetReportQuery.data || targetReportQuery.isPlaceholderData),
      lockedCount: lockedCount,
      pageLockedCount,
      getRowSelectionDisabledReason: (row) => {
        const entity =
          isNegativeKeyword ? t(($) => $.advertising["negative keyword"])
          : queryTargetType === "target" ? t(($) => $.advertising["target"])
          : t(($) => $.advertising["keyword target"]);
        if (row.original.lockout) {
          return t(
            ($) =>
              $.advertising[
                "This {entity} is locked because it already has an update pending from a bulk action. It will be selectable again once that update completes."
              ],
            { entity },
          );
        }
        if (
          row.original.match_type === "theme" &&
          ![AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(adPlanData.ad_type)
        ) {
          return t(($) => $.advertising["Theme targets can't be selected for bulk actions."]);
        }
        return undefined;
      },
      columnFilterSelectOptions: {
        state: TARGET_STATE_OPTIONS,
        match_type:
          isNegativeKeyword ? negativeKeywordMatchTypeOptions
          : targetType === "keyword" ? KEYWORD_MATCH_TYPE_OPTIONS
          : TARGET_MATCH_TYPE_OPTIONS,
        source: channel === "walmart" ? WALMART_TARGET_SOURCE_OPTIONS : AMAZON_TARGET_SOURCE_OPTIONS,
        target_bid_state: targetBidStateOptions,
        has_manual_bid: [
          { value: true, label: t(($) => $.advertising["Has Fixed Bid"]) },
          { value: false, label: t(($) => $.advertising["No Fixed Bid"]) },
        ],
        tags: tagOptions,
        "tags_enhanced.id": tagEnhancedOptions,
      },
    },
  });

  const selectedIds = selectionStateToSelectedIds(table.state.rowSelection).map((id) => parseInt(id));
  const selected = state.isAllMatchingSelected ? ("all" as const) : selectedIds;

  const resetSelection = () => {
    table.resetRowSelection();
    setSelectedRowsData({});
  };

  const { bulkActionOptions: allBulkActionOptions } = useTargetBulkActions(
    selected,
    resetSelection,
    queryTargetType,
    { keywords: Object.values(selectedRowsData) },
    isNTMAd,
    isTrellisManagedAd,
    { filters: combinedFilters, sorting: state.sorting, grouping: dimensions, search: debounceSearch },
  );

  const bulkActionOptions =
    isNegativeKeyword ?
      allBulkActionOptions.filter((opt: { group?: string }) => opt.group === "State" || opt.group === "Tags")
    : allBulkActionOptions;

  const showAddTargets =
    ((isNTMAd && NTMBidsAccepted) || !isNTMAd) &&
    !(channel === "walmart" && queryTargetType === "target") &&
    userCanUpdateAds &&
    !simulationMode;

  const isAdEnded = adPlanData.serving_status === "ENDED";

  const showSimulateButton = userCanUpdateAds && !isNegativeKeyword && isTrellisManagedAd && !simulationMode;

  return (
    <EditableTableWrapper formOptions={{ readPretty: isNegativeKeyword || !userCanUpdateAds || simulationMode }}>
      <div className={css({ maxWidth: "88vw" })}>
        <Wrap
          gap="medium"
          className={css({
            justifyContent: "end",
            alignItems: "center",
            width: "100%",
            maxWidth: "100%",
            padding: "small",
            fontSize: "large",
          })}
        >
          {!simulationMode && userCanUpdateAds && !isNegativeKeyword && (
            <EditableTableSubmitArea<{ bid_resolved?: number }> onSubmit={submitBids} />
          )}
          {simulationMode && hasResults && (
            <Button
              isPending={isDeploying}
              isDisabled={isSimulating}
              onPress={() => void handleDeploy()}
              tooltip={t(($) => $.advertising["Apply simulated bids to your targets and update media plan settings."])}
            >
              {t(($) => $.common.actions.Apply)}
            </Button>
          )}
          {simulationMode && (
            <Button variant="secondary" onPress={exitSimulation}>
              {t(($) => $.advertising["Exit Simulation"])}
            </Button>
          )}
          {showSimulateButton && (
            <Button variant="secondary" onPress={enterSimulation}>
              {t(($) => $.advertising["Simulate Bids"])}
            </Button>
          )}
          {showPreviewBid && (
            <Button
              variant="secondary"
              isPending={updateAdPlan.isPending}
              onPress={() => updateAdPlan.mutate({ adPlan: adPlanParams })}
            >
              {t(($) => $.advertising["Refresh Bid Preview"])}
            </Button>
          )}
          {showAddTargets && (
            <AddTargetsWidget
              key={`${adId}${targetType}`}
              adId={adId}
              adType={adPlanData.ad_type}
              targetType={queryTargetType}
              isNTMAd={isNTMAd}
              negativeOnly={isNegativeKeyword}
            />
          )}
          {userCanUpdateAds && !isAdEnded && !simulationMode && (
            <BulkActionButton
              options={bulkActionOptions}
              selected={selected}
              selectedCount={state.isAllMatchingSelected ? table.getRowCount() - lockedCount : undefined}
            />
          )}
        </Wrap>
        {simulationMode && (
          <SimulationParametersForm
            initialValues={simulationInitialValues}
            onApply={handleApplySimulation}
            isPending={isSimulating}
          />
        )}
        <HStack width="100%" padding="small" fontSize="large">
          <VStack flex="1" alignItems="start">
            <HStack width="100%">
              <Button
                variant="contentOnly"
                onPress={() =>
                  resetTable(table, targetType === "keyword" ? "keywordTargetReport" : "productTargetReport")
                }
                fontSize="xxlarge"
              >
                <MdRotateLeft />
              </Button>
              <RT8ColumnSelector table={table} />
              {targetType === "keyword" ?
                <DataViewPicker viewKey="keywordTargetReport" datasetKey="keyword" table={table} />
              : <DataViewPicker viewKey="productTargetReport" datasetKey="target" table={table} />}
              <SearchInput
                aria-label={t(($) => $.common.actions.Search)}
                isExpandable
                css={{ flex: "0 1 350px" }}
                value={table.state.globalFilter}
                onChange={table.setGlobalFilter}
              />
            </HStack>
            <RT8FilterWidget table={table} />
          </VStack>
          <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
        </HStack>
      </div>
      <TableErrorWrapper error={targetReportQuery.error} userMessage={t(($) => $.advertising["Error loading stats"])}>
        <div className={css({ width: "100%", maxWidth: "100vw", overflowX: "auto" })}>
          <Table
            table={table}
            className={css({ width: "100%", minWidth: "max-content" })}
            renderExpander={(row) => {
              const targetId = row.original[idField];
              const formattedRowData = {
                _original: {
                  ...row.original,
                  target_id: targetId ?? null,
                },
              };

              return (
                <TargetDetails
                  row={formattedRowData}
                  marketPlace={marketPlace}
                  channel={channel}
                  targetType={queryTargetType}
                />
              );
            }}
          />
        </div>
      </TableErrorWrapper>
      {userCanExportAds && (
        <HStack width="100%" padding="small">
          <DownloadV4Report downloadMutation={exportMutation} />
        </HStack>
      )}
    </EditableTableWrapper>
  );
};
